import React, { useState } from 'react';
import {
  Drawer,
  makeStyles,
  MenuList,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Grid,
  Typography,
  useTheme,
  useMediaQuery,
  Collapse
} from '@material-ui/core';
import { useSelector, useDispatch } from 'react-redux';
import { appProfileSelector } from 'redux/selectors';
import classNames from 'classnames';
import GetMenus from './menus';
import useRouter from 'utils/useRouter';
import { logout } from 'redux/actions/auth';
import Notifications from '../Notifications'
import { logoURL, VERSION } from 'config';
import PropTypes from 'prop-types';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

export const drawerWidth = 230;
export const drawerCloseWidth = 60;

const useStyles = makeStyles(theme => ({
  drawerPaper: {
    width: drawerWidth,
    border: 'none',
    //borderTopRightRadius: theme.spacing(4),
    //borderBottomRightRadius: theme.spacing(4),
    overflowY: 'hidden',
    overflowX: 'hidden',
    [theme.breakpoints.up('md')]: {
      width: drawerWidth,
      border: 'none',
      position: 'relative',
    },
    backgroundColor: theme.palette.app.primary,
    transition: 'all .5s ease',
  },
  drawerClosePaper: {
    width: drawerCloseWidth,
    border: 'none',
    overflowY: 'hidden',
    overflowX: 'hidden',
    [theme.breakpoints.up('md')]: {
      width: drawerCloseWidth,
      border: 'none',
      position: 'relative',
    },
    backgroundColor: theme.palette.app.primary,
    transition: 'all .5s ease',
  },
  drawerContainer: {
    position: 'fixed',
    left: 0,
    top: 0,
    bottom: 0,
    zIndex: 2,
    transition: 'all .5s ease',
  },
  drawerInner: {
    width: drawerWidth,
    height: '100%',
  },
  menuList: {
    height: '90%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    maxHeight: 800,
    minHeight: 500,
    // keep original layout behavior; do not force vertical scrollbar here
    overflowX: 'hidden',
  },
  popoverRow: {
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: theme.palette.app.primary,
    color: '#fff',
    padding: theme.spacing(1),
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[4],
    // slide in left->right
    transformOrigin: 'left center',
    marginTop : '5px',
    animation: `$popin 400ms ${theme.transitions.easing.easeOut}`,
    '& > button': {
      margin: theme.spacing(0, 0.5),
      color: '#fff',
      background: 'transparent',
      border: '1px solid rgba(255,255,255,0.06)',
      padding: theme.spacing(1, 2),
      borderRadius: 6,
      cursor: 'pointer'
    }
  },
  '@keyframes popin': {
    '0%': {
      transform: 'translateX(-300px)',
      opacity: 1
    },
    '100%': {
      transform: 'translateX(0)',
      opacity: 1
    }
  },
  link: {
    textDecoration: 'none',
    background: 'none',
    border: 'none',
    width: '100%',
    padding: 0,
    cursor: 'pointer',
    color: theme.palette.app.primary,
    wordBreak: 'break-all',
  },
  listItemText: {
    fontSize: 14,
  },
  logo: {
    maxWidth: 160,
    maxHeight: 100,
    backgroundColor: 'white',
    borderRadius: '0px 0px 30px 30px'
  },
  activebar: {
    position: 'absolute',
    left: 0,
    width: 3,
    top: 0,
    bottom: 0,
    zIndex: 1,
    backgroundColor: 'white'
  },
  closeSideMenuItem: {
    paddingLeft: '21px !important'
  },
  submenuitem: {
    paddingTop: '4px !important',
    paddingBottom: '4px !important',
  }
}));

const NavBar = ({ openDrawer, setOpenDrawer }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xs'));
  const profile = useSelector(appProfileSelector);
  const { role, companyLogo, logo } = profile
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const menus = GetMenus(role);
  const router = useRouter();
  const pathname = String(router.history.location.pathname).toLowerCase();
  const dispatch = useDispatch();
  const isActive = item => {
    return !open && pathname.includes(String(item.id).toLowerCase());
  };
  const onSignOut = () => {
    dispatch(logout());
  };
  const openNewUrl = (link) => {
    const win = window.open(link, "_blank");
    win.focus();
  }
  const logoImage = companyLogo || logo;
  // on xs screens we render the Drawer as temporary overlay (modal) and controlled by openDrawer
  const variant = isXs ? 'temporary' : 'permanent';
  const drawerOpen = isXs ? openDrawer : true;
  return (
    <Drawer
      id="sidebar-menu"
      anchor="left"
      open={drawerOpen}
      classes={{ paper: openDrawer ? classes.drawerPaper : classes.drawerClosePaper, root: classes.drawerContainer }}
      ModalProps={{
        keepMounted: true,
      }}
      variant={variant}
      onClose={() => { if (isXs) setOpenDrawer(false); }}
      {...(isXs ? {} : {
        onMouseOver: (e) => {
          e.preventDefault();
          if (!isXs) setOpenDrawer(true)
        },
        onMouseLeave: (e) => {
          e.preventDefault();
          if (!isXs) setOpenDrawer(false)
        }
      })}
    >
      <div className={classes.drawerInner}>
        <div className={classes.menuList}>
          {
            openDrawer && logoImage ?
              <Grid container justify="center">
                <img src={logoURL + logoImage} alt="apave" className={classes.logo} />
              </Grid> :
              <div style={{ height: 75 }} />
          }
          <MenuList>
            {menus.middle.map((item, index) => (
              <button
                key={`id-${item.id}`}
                className={classNames(classes.link, 'side_menu-item')}
                onClick={() => {
                  window.dataLayer.push({
                    'event': 'navigation',
                    'navigation_template': "Menu",
                    'navigation_path': window.location.href,
                    'click_name': item.id.toUpperCase()
                  });
                  if (item.href) {
                    openNewUrl(item.href)
                    return
                  }
                  router.history.push(item.url);
                  // close overlay drawer on small screens after navigation
                  if (isXs && setOpenDrawer) setOpenDrawer(false);
                }}
                tabIndex={index}
              >
                {
                  isActive(item) && <div className={classes.activebar} />
                }
                <MenuItem
                  className={classNames(classes.menuitem, isActive(item) ? 'menu-item--selected' : '', { [classes.closeSideMenuItem]: !openDrawer })}
                >
                  <ListItemIcon className={'menu-icon'}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    inset
                    disableTypography
                    className={classNames(
                      classes.listItemText,
                      'truncate-list-item-text',
                      'menu-text',
                    )}
                  />
                </MenuItem>
              </button>
            ))}
          </MenuList>
          <MenuList>
            {menus.bottom.map((item, index) => (
                item.children ?
                  <SubMenu
                    key={`parent-${item.id}`}
                    item={item}
                    openNewUrl={openNewUrl}
                    isActive={isActive}
                    openDrawer={openDrawer}
                    isXs={isXs}
                    setOpenDrawer={setOpenDrawer}
                  /> :
                <button
                  key={`id-${item.id}`}
                  className={classNames(classes.link, 'side_menu-item')}
                  onClick={() => {
                    window.dataLayer.push({
                      'event': 'navigation',
                      'navigation_template': "Menu",
                      'navigation_path': window.location.href,
                      'click_name': item.id.toUpperCase()
                    });
                    if (item.href) {
                      openNewUrl(item.href)
                      return
                    }
                    if (item.id === 'exit') {
                      window.dataLayer.push({
                        'event': 'logout'
                      });
                      return onSignOut();
                    }
                    router.history.push(item.url);
                    if (isXs && setOpenDrawer) setOpenDrawer(false);
                  }}
                  tabIndex={index}
                >
                  {
                    isActive(item) && <div className={classes.activebar} />
                  }
                  <MenuItem
                    className={classNames(isActive(item) ? 'menu-item--selected' : '', { [classes.closeSideMenuItem]: !openDrawer })}
                  >
                    <ListItemIcon className={'menu-icon'}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      inset
                      disableTypography
                      className={classNames(
                        classes.listItemText,
                        'truncate-list-item-text',
                        'menu-text',
                      )}
                    />
                  </MenuItem>
                </button>
            ))}
          </MenuList>
        </div>
        <Typography style={{
          color: "white",
          position: "absolute",
          bottom: 5,
          left: openDrawer ? 90 : 18,
          fontSize: 10
        }}>{VERSION}</Typography>
      </div>
      <Notifications
        id={id}
        open={open}
        anchorEl={anchorEl}
        handleClose={handleClose}
      />
    </Drawer>
  );
};
NavBar.propTypes = {
  openDrawer: PropTypes.bool,
  setOpenDrawer: PropTypes.func
};
export default NavBar;


const SubMenu = ({ item, openNewUrl, isActive, openDrawer, isXs, setOpenDrawer }) => {
  const classes = useStyles();
  const router = useRouter();
  const [expand, setExpand] = useState(isActive(item));

  return <div>
    <button
      key={`id-${item.id}`}
      className={classNames(classes.link, 'side_menu-item')}
      onClick={(e) => {
        if (item.children) {
          // Always toggle expand inline (no small-screen popover)
          setExpand(prev => !prev)
          return
        }
        if (item.href) {
          openNewUrl(item.href)
          return
        }
        if (item.id == 'apidoc') {
          window.open(item.url, "_blank");
          return;
        }
        router.history.push(item.url);
        // close overlay drawer on small screens after navigation
        if (isXs && setOpenDrawer) setOpenDrawer(false);
      }}
    >
      {
        isActive(item) && <div className={classes.activebar} />
      }
      <CustomMenuItem
        item={item}
        isActive={isActive}
        expand={expand}
        setExpand={setExpand}
        openDrawer={openDrawer}
      />
    </button>
    {/* original expand behavior for md+ where drawer is expanded — animated */}
    <Collapse in={Boolean(expand && item.children && openDrawer)} timeout="auto" unmountOnExit>
      <div className={classes.submenuitem}>
        {
          item.children.map(el => {
            return <CollapseMenu
              key={el.id}
              item={el}
              openNewUrl={openNewUrl}
              isActive={isActive}
              openDrawer={openDrawer}
              setOpenDrawer={setOpenDrawer}
              isXs={isXs}
            />
          })
        }
      </div>
    </Collapse>
  </div>
}
SubMenu.propTypes = {
  item: PropTypes.any,
  openNewUrl: PropTypes.any,
  isActive: PropTypes.bool,
  openDrawer: PropTypes.bool,
  isXs: PropTypes.bool,
  setOpenDrawer: PropTypes.func
};
SubMenu.propTypes = { ...SubMenu.propTypes, isXs: PropTypes.bool, setOpenDrawer: PropTypes.func };

const CollapseMenu = ({ item, openNewUrl, isActive, openDrawer, setOpenDrawer, isXs }) => {
  const classes = useStyles();
  const router = useRouter();
  const [expand, setExpand] = useState(isActive(item))
  // const dispatch = useDispatch()

  return <div>
    <button
      key={`id-${item.id}`}
      className={classNames(classes.link, 'side_menu-item')}
      onClick={() => {
        if(item.id == "contact"){
          window.open("https://ressources.apave.com/fr/espace-clients-demande-de-contact", "_blank", "noopener")
          return;
        }
        if(item.children){
          setExpand(prev => !prev)
          return
        }
        if (item.href) {
          openNewUrl(item.href)
          return
        }
        router.history.push(item.url);
        if (isXs && setOpenDrawer) setOpenDrawer(false);
      }}
    >
      <CustomMenuItem
        item={item}
        isActive={isActive}
        expand={expand}
        setExpand={setExpand}
        openDrawer={openDrawer}
      />
    </button>
  </div>
}
CollapseMenu.propTypes = {
  item: PropTypes.any,
  openNewUrl: PropTypes.any,
  isActive: PropTypes.bool,
  openDrawer: PropTypes.bool,
  isXs: PropTypes.bool,
  setOpenDrawer: PropTypes.func
};
CollapseMenu.propTypes = { ...CollapseMenu.propTypes, setOpenDrawer: PropTypes.func, isXs: PropTypes.bool };

const CustomMenuItem = ({ item, isActive, expand, openDrawer, setExpand }) => {
  const classes = useStyles();
  return <MenuItem
    className={classNames(classes.menuitem, isActive(item) ? 'menu-item--selected' : '', { [classes.closeSideMenuItem]: !openDrawer })}
  >
    <ListItemIcon className={'menu-icon'}>
      {item.icon}
    </ListItemIcon>
    <ListItemText
      primary={item.text}
      inset
      disableTypography
      className={classNames(
        'truncate-list-item-text',
        'menu-text',
      )}
    />
    {
      item.children &&
      <>
        {
          expand ?
            <ExpandLessIcon style={{ fill: "white" }} onClick={e => {
              e.stopPropagation();
              setExpand(false)
            }} /> :
            <ExpandMoreIcon style={{ fill: "white" }} onClick={e => {
              e.stopPropagation();
              setExpand(true)
            }} />
        }
      </>
    }
  </MenuItem>
}

CustomMenuItem.propTypes = {
  item: PropTypes.any,
  isActive: PropTypes.bool,
  expand: PropTypes.bool,
  openDrawer: PropTypes.bool,
  setExpand: PropTypes.func
};